import { persistedStorage } from '@platform/design-system';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { LABEL_FORMATS, SYMBOLOGIES, type LabelFormat, type Symbology } from './labels.ts';

export type LabelPreferences = {
  readonly format: LabelFormat;
  readonly symbology: Symbology;
  readonly showPrice: boolean;
  readonly choose: (changes: Partial<Pick<LabelPreferences, 'format' | 'symbology' | 'showPrice'>>) => void;
};

export const LABEL_PREFERENCES_KEY = 'admin.labels';

const DEFAULTS = { format: 'sheet', symbology: 'code128', showPrice: true } as const;

const oneOf = <Value extends string>(values: readonly Value[], stored: unknown, fallback: Value): Value =>
  values.find((value) => value === stored) ?? fallback;

export const useLabelPreferences = create<LabelPreferences>()(
  persist(
    (set) => ({
      ...DEFAULTS,
      choose: (changes) => {
        set(changes);
      },
    }),
    {
      name: LABEL_PREFERENCES_KEY,
      storage: persistedStorage(),
      partialize: (state) => ({
        format: state.format,
        symbology: state.symbology,
        showPrice: state.showPrice,
      }),
      merge: (persisted, current) => {
        const stored = persisted as Partial<LabelPreferences> | undefined;
        return {
          ...current,
          format: oneOf(LABEL_FORMATS, stored?.format, DEFAULTS.format),
          symbology: oneOf(SYMBOLOGIES, stored?.symbology, DEFAULTS.symbology),
          showPrice: typeof stored?.showPrice === 'boolean' ? stored.showPrice : DEFAULTS.showPrice,
        };
      },
    },
  ),
);
